import {getConfig, once} from './core.js';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SCALE_STEP = 0.5;

function createButton(className, label, iconClass) {
	const button = document.createElement('button');
	button.type = 'button';
	button.className = className;
	button.setAttribute('aria-label', label);
	button.title = label;
	const icon = document.createElement('i');
	icon.className = iconClass;
	icon.setAttribute('aria-hidden', 'true');
	button.append(icon);
	return button;
}

function getItems(gallery) {
	const seen = new Set();
	return [...gallery.querySelectorAll('#obrazek[href], #thumbs_list a.gallery[href]')]
		.map((link) => ({
			link,
			href: link.href,
			alt: link.querySelector('img')?.alt || link.title || '',
		}))
		.filter((item) => {
			if (seen.has(item.href)) {
				return false;
			}
			seen.add(item.href);
			return true;
		});
}

function bindGallery(gallery) {
	if (!once(gallery, 'gallery')) {
		return;
	}
	const links = [...gallery.querySelectorAll('#obrazek[href], #thumbs_list a.gallery[href]')];
	const items = getItems(gallery);
	if (items.length === 0) {
		return;
	}
	const config = getConfig().productGallery || {};
	const text = {
		close: config.close || 'Close',
		zoomIn: config.zoomIn || 'Zoom in',
		zoomOut: config.zoomOut || 'Zoom out',
		zoomHelp: config.zoomHelp || '',
		previous: config.previous || 'Previous',
		next: config.next || 'Next',
		loading: config.loading || 'Loading…',
		error: config.error || 'Image could not be loaded.',
	};

	const overlay = document.createElement('div');
	overlay.className = 'product-lightbox';
	overlay.hidden = true;
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');
	const captionId = `product-lightbox-caption-${Math.random().toString(36).slice(2)}`;
	overlay.setAttribute('aria-labelledby', captionId);

	const stage = document.createElement('div');
	stage.className = 'product-lightbox__stage';
	const image = document.createElement('img');
	image.className = 'product-lightbox__image';
	image.draggable = false;
	const loader = document.createElement('div');
	loader.className = 'product-lightbox__loader';
	loader.setAttribute('role', 'status');
	const loaderText = document.createElement('span');
	loaderText.className = 'sr-only';
	loaderText.textContent = text.loading;
	loader.append(loaderText);
	stage.append(image, loader);

	const close = createButton('product-lightbox__button product-lightbox__close', text.close, 'bi bi-x-lg');
	const previous = createButton('product-lightbox__button product-lightbox__previous', text.previous, 'bi bi-chevron-left');
	const next = createButton('product-lightbox__button product-lightbox__next', text.next, 'bi bi-chevron-right');
	const zoomOut = createButton('product-lightbox__button', text.zoomOut, 'bi bi-dash-circle');
	const zoomInLabel = [text.zoomIn, text.zoomHelp].filter(Boolean).join(' — ');
	const zoomIn = createButton('product-lightbox__button', zoomInLabel, 'bi bi-plus-circle');
	const toolbar = document.createElement('div');
	toolbar.className = 'product-lightbox__toolbar';
	toolbar.append(zoomIn, zoomOut);

	const footer = document.createElement('div');
	footer.className = 'product-lightbox__footer';
	const caption = document.createElement('span');
	caption.id = captionId;
	caption.className = 'product-lightbox__caption';
	const counter = document.createElement('span');
	counter.className = 'product-lightbox__counter';
	const zoomStatus = document.createElement('span');
	zoomStatus.className = 'product-lightbox__zoom-status';
	zoomStatus.setAttribute('aria-live', 'polite');
	footer.append(caption, counter, zoomStatus);
	overlay.append(stage, close, previous, next, toolbar, footer);
	document.body.append(overlay);

	let currentIndex = 0;
	let scale = MIN_SCALE;
	let offsetX = 0;
	let offsetY = 0;
	let opener = null;
	let drag = null;

	const clampOffsets = () => {
		if (scale <= MIN_SCALE) {
			offsetX = 0;
			offsetY = 0;
			return;
		}
		const maxX = Math.max(0, (image.clientWidth * scale - stage.clientWidth) / 2);
		const maxY = Math.max(0, (image.clientHeight * scale - stage.clientHeight) / 2);
		offsetX = Math.max(-maxX, Math.min(maxX, offsetX));
		offsetY = Math.max(-maxY, Math.min(maxY, offsetY));
	};

	const renderTransform = () => {
		clampOffsets();
		image.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0) scale(${scale})`;
		image.classList.toggle('is-zoomed', scale > MIN_SCALE);
		zoomOut.disabled = scale <= MIN_SCALE;
		zoomIn.disabled = scale >= MAX_SCALE;
		zoomStatus.textContent = `${Math.round(scale * 100)} %`;
	};

	const setScale = (value) => {
		scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, value));
		if (scale === MIN_SCALE) {
			offsetX = 0;
			offsetY = 0;
		}
		renderTransform();
	};

	const showItem = (index) => {
		currentIndex = Math.max(0, Math.min(items.length - 1, index));
		const item = items[currentIndex];
		scale = MIN_SCALE;
		offsetX = 0;
		offsetY = 0;
		image.hidden = true;
		loader.hidden = false;
		overlay.setAttribute('aria-busy', 'true');
		caption.textContent = item.alt;
		counter.textContent = `${currentIndex + 1} / ${items.length}`;
		previous.disabled = currentIndex === 0;
		next.disabled = currentIndex === items.length - 1;
		previous.hidden = items.length < 2;
		next.hidden = items.length < 2;
		image.onload = () => {
			image.hidden = false;
			loader.hidden = true;
			overlay.removeAttribute('aria-busy');
			renderTransform();
		};
		image.onerror = () => {
			loader.hidden = true;
			overlay.removeAttribute('aria-busy');
			caption.textContent = text.error;
		};
		image.alt = item.alt;
		image.src = item.href;
	};

	const open = (item, trigger) => {
		opener = trigger;
		overlay.hidden = false;
		document.body.classList.add('modal-open');
		showItem(Math.max(0, items.indexOf(item)));
		close.focus();
	};

	const closeGallery = () => {
		if (overlay.hidden) {
			return;
		}
		overlay.hidden = true;
		if (!document.querySelector('.modal.show')) {
			document.body.classList.remove('modal-open');
		}
		opener?.focus();
	};

	links.forEach((link) => {
		link.addEventListener('click', (event) => {
			event.preventDefault();
			open(items.find((item) => item.href === link.href) || items[0], link);
		});
	});

	close.addEventListener('click', closeGallery);
	previous.addEventListener('click', () => showItem(currentIndex - 1));
	next.addEventListener('click', () => showItem(currentIndex + 1));
	zoomIn.addEventListener('click', () => setScale(scale + SCALE_STEP));
	zoomOut.addEventListener('click', () => setScale(scale - SCALE_STEP));
	overlay.addEventListener('click', (event) => {
		if (event.target === overlay) {
			closeGallery();
		}
	});
	stage.addEventListener('wheel', (event) => {
		event.preventDefault();
		setScale(scale + (event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP));
	}, {passive: false});
	image.addEventListener('dblclick', () => setScale(scale === MIN_SCALE ? 2 : MIN_SCALE));
	image.addEventListener('pointerdown', (event) => {
		if (scale <= MIN_SCALE) {
			return;
		}
		drag = {x: event.clientX, y: event.clientY, offsetX, offsetY};
		image.setPointerCapture(event.pointerId);
		image.classList.add('is-dragging');
	});
	image.addEventListener('pointermove', (event) => {
		if (!drag) {
			return;
		}
		offsetX = drag.offsetX + event.clientX - drag.x;
		offsetY = drag.offsetY + event.clientY - drag.y;
		renderTransform();
	});
	const stopDragging = () => {
		drag = null;
		image.classList.remove('is-dragging');
	};
	image.addEventListener('pointerup', stopDragging);
	image.addEventListener('pointercancel', stopDragging);
	window.addEventListener('resize', renderTransform);
	document.addEventListener('keydown', (event) => {
		if (overlay.hidden) {
			return;
		}
		if (event.key === 'Escape') {
			event.preventDefault();
			closeGallery();
		} else if (event.key === 'ArrowLeft' && currentIndex > 0) {
			event.preventDefault();
			showItem(currentIndex - 1);
		} else if (event.key === 'ArrowRight' && currentIndex < items.length - 1) {
			event.preventDefault();
			showItem(currentIndex + 1);
		} else if (event.key === '+' || event.key === '=') {
			event.preventDefault();
			setScale(scale + SCALE_STEP);
		} else if (event.key === '-') {
			event.preventDefault();
			setScale(scale - SCALE_STEP);
		} else if (event.key === 'Tab') {
			const focusable = [...overlay.querySelectorAll('button:not([hidden]):not(:disabled)')];
			const first = focusable[0];
			const last = focusable.at(-1);
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		}
	});
}

export function init(root = document) {
	root.querySelectorAll('[data-product-gallery]').forEach(bindGallery);
	root.querySelectorAll('[data-dismiss-closest]').forEach((button) => {
		if (once(button, 'dismiss-closest')) {
			button.addEventListener('click', () => button.closest(button.dataset.dismissClosest)?.remove());
		}
	});
}
